/**
 * Pricing.tsx — Public pricing page.
 *
 * Route: /pricing — accessible to all visitors.
 * Monthly/annual toggle (annual saves 20%), 3 plan cards, 7-question FAQ, CTA.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE from '../config/api';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SecurityIcon from '@mui/icons-material/Security';

// ── Plan data fetched from /api/plans ─────────────────────────────────────────

function makeFAQ(trialDays: number, annualDiscount: number) {
  return [
  {
    q: 'Is there really no credit card required for the trial?',
    a: `You can use Shield free for ${trialDays} days. We collect your card at signup but will not charge it until your trial ends.`,
  },
  {
    q: 'What happens when my trial expires?',
    a: 'Your account is paused — not deleted. Your data is kept safe for 30 days after trial expiry, giving you time to add a payment method and pick up exactly where you left off.',
  },
  {
    q: 'Can I change plans after signing up?',
    a: 'Yes, you can upgrade or downgrade at any time from your Billing page. Upgrades take effect immediately; downgrades apply at the end of your current billing period.',
  },
  {
    q: 'Do you offer discounts for annual billing?',
    a: `Yes — switching to annual billing saves you ${annualDiscount}% compared to paying month-to-month. You can toggle this on the billing page at any time.`,
  },
  {
    q: 'What cloud providers does Shield support?',
    a: 'Shield currently supports AWS, Google Cloud Platform, and Microsoft Azure. On-premise servers via SSH are also supported on Growth and Professional plans.',
  },
  {
    q: 'Is my data secure? Where is it stored?',
    a: 'All data is encrypted at rest (AES-256) and in transit (TLS 1.3). We store data in the EU and US and are SOC2 Type II certified. We never store your actual cloud data — only scan results and metadata.',
  },
  {
    q: 'Do you offer custom enterprise plans?',
    a: 'Yes. If you have more than 200 employees, need custom SLAs, on-premise deployment, or a dedicated account manager, contact our sales team at sales@[CONTACT EMAIL].',
  },
  ]; // end array
} // end makeFAQ

// ── Plan card ─────────────────────────────────────────────────────────────────

function PlanCard({ plan, annual, onStart }: { plan: any; annual: boolean; onStart: () => void }) {
  const displayPrice = annual ? plan.price_annually_monthly : plan.price_monthly;
  const annualSavings = annual ? Math.round(plan.annual_savings ?? 0) : 0;

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: plan.is_popular ? '2px solid' : '1px solid',
        borderColor: plan.is_popular ? 'primary.main' : 'rgba(255,255,255,0.08)',
        bgcolor: plan.is_popular ? 'rgba(0,180,216,0.05)' : 'background.paper',
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s',
        '&:hover': { transform: 'translateY(-2px)' },
      }}
    >
      {plan.is_popular && (
        <Box sx={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)' }}>
          <Chip label="⭐ Most Popular" size="small" color="primary" sx={{ fontWeight: 700, fontSize: 11 }} />
        </Box>
      )}

      <CardContent sx={{ p: 3.5, pt: plan.is_popular ? 5 : 3.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" fontWeight={700} mb={0.5}>{plan.display_name}</Typography>
        <Typography variant="body2" color="text.secondary" mb={2.5}>{plan.tagline ?? ''}</Typography>

        <Box sx={{ mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography variant="h3" fontWeight={900} color="primary.main">
              ${Math.round(displayPrice)}
            </Typography>
            <Typography variant="body2" color="text.secondary">/month</Typography>
          </Box>
          {annual && annualSavings > 0 && (
            <Typography variant="caption" color="success.main" fontWeight={600}>
              Billed annually — save ${annualSavings}/yr
            </Typography>
          )}
        </Box>

        <Button
          fullWidth
          variant={plan.is_popular ? 'contained' : 'outlined'}
          onClick={onStart}
          sx={{ fontWeight: 700, mb: 3 }}
        >
          Start Free Trial
        </Button>

        <Divider sx={{ mb: 2.5 }} />

        <Stack spacing={1.25} sx={{ flex: 1 }}>
          {(plan.features as string[]).map((text) => (
            <Box key={text} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <CheckIcon sx={{ fontSize: 17, color: 'success.main', flexShrink: 0 }} />
              <Typography variant="body2">{text}</Typography>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Pricing() {
  const navigate  = useNavigate();
  const [annual, setAnnual] = useState(false);
  const [plans, setPlans]   = useState<any[]>([]);
  const trialDays = plans[0]?.trial_days ?? 14;
  const annualDiscount = plans[0]?.annual_discount_pct ?? 20;

  useEffect(() => {
    axios.get(`${API_BASE}/plans`).then((r) => setPlans(r.data)).catch(console.error);
  }, []);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>

      {/* ── Navbar ─────────────────────────────────────────────────────── */}
      <Box
        component="nav"
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: { xs: 2, sm: 5 }, py: 2,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          position: 'sticky', top: 0, zIndex: 10,
          bgcolor: 'rgba(13,17,23,0.92)', backdropFilter: 'blur(12px)',
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <SecurityIcon sx={{ color: '#00E5FF', fontSize: 26, filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.6))' }} />
          <Typography variant="h6" fontWeight={900} sx={{ color: '#00E5FF', textShadow: '0 0 12px rgba(0,229,255,0.4)' }}>Shield</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="text" onClick={() => navigate('/login')} sx={{ color: 'text.secondary' }}>
            Sign in
          </Button>
          <Button variant="contained" onClick={() => navigate('/signup')} size="small" sx={{ fontWeight: 700 }}>
            Start Free Trial
          </Button>
        </Stack>
      </Box>

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <Box sx={{ textAlign: 'center', pt: { xs: 6, md: 9 }, pb: 5, px: 3 }}>
        <Typography variant="h3" fontWeight={800} mb={2}>
          Simple, honest pricing
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 540, mx: 'auto', fontWeight: 400, lineHeight: 1.6, mb: 4 }}>
          {trialDays}-day free trial on all plans — no credit card required.
          Cancel anytime.
        </Typography>

        {/* Annual/monthly toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
          <Typography variant="body2" color={annual ? 'text.secondary' : 'text.primary'} fontWeight={500}>
            Monthly
          </Typography>
          <Switch
            checked={annual}
            onChange={(e) => setAnnual(e.target.checked)}
            color="primary"
          />
          <Typography variant="body2" color={annual ? 'text.primary' : 'text.secondary'} fontWeight={500}>
            Annual
          </Typography>
          <Chip label="Save 20%" size="small" color="success" sx={{ fontWeight: 700, fontSize: 11 }} />
        </Box>
      </Box>

      {/* ── Plan cards ─────────────────────────────────────────────────── */}
      <Box sx={{ px: { xs: 2, sm: 4 }, pb: 10, maxWidth: 1060, mx: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 3,
            mb: 10,
          }}
        >
          {plans.map((plan) => (
            <PlanCard
              key={plan.plan_key}
              plan={plan}
              annual={annual}
              onStart={() => navigate(`/signup?plan=${plan.plan_key}`)}
            />
          ))}
        </Box>

        {/* ── Enterprise ─────────────────────────────────────────────────── */}
        <Box
          sx={{
            borderRadius: 3,
            border: '1px solid rgba(247,127,0,0.3)',
            bgcolor: 'rgba(247,127,0,0.04)',
            p: { xs: 3, sm: 5 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { md: 'center' },
            justifyContent: 'space-between',
            gap: 3,
            mb: 10,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} mb={0.75} color="secondary.main">
              Enterprise & custom plans
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
              More than 200 employees? Need custom SLAs, on-premise deployment, a dedicated account manager,
              or volume pricing? Talk to our sales team.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => navigate('/contact')}
            sx={{ fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Contact Sales
          </Button>
        </Box>

        {/* ── FAQ ────────────────────────────────────────────────────────── */}
        <Box maxWidth={720} mx="auto" mb={10}>
          <Typography variant="h4" fontWeight={800} textAlign="center" mb={5}>
            Frequently asked questions
          </Typography>
          {makeFAQ(trialDays, annualDiscount).map(({ q, a }) => (
            <Accordion
              key={q}
              disableGutters
              elevation={0}
              sx={{
                bgcolor: 'transparent',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '8px !important',
                mb: 1.5,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3 }}>
                <Typography variant="body1" fontWeight={600}>{q}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 2.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* ── CTA ────────────────────────────────────────────────────────── */}
        <Box
          sx={{
            textAlign: 'center',
            borderRadius: 3,
            border: '1px solid rgba(0,180,216,0.2)',
            bgcolor: 'rgba(0,180,216,0.04)',
            py: { xs: 6, md: 8 },
            px: 4,
          }}
        >
          <Typography variant="h4" fontWeight={800} mb={2}>
            Ready to secure your cloud?
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={4} sx={{ maxWidth: 480, mx: 'auto' }}>
            Join hundreds of companies protecting their infrastructure with Shield.
            No credit card. No commitment. Cancel anytime.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/signup')}
              sx={{ fontWeight: 700, px: 5, py: 1.6 }}
            >
              Start Your Free Trial
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/contact')}
              sx={{ fontWeight: 600, px: 4 }}
            >
              Talk to Sales
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <Box component="footer" sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.disabled">
          © {new Date().getFullYear()} Shield Security · All rights reserved
        </Typography>
      </Box>
    </Box>
  );
}
